import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/auth";
import {
  ArrowLeft, MessageSquare, Send, Loader2, Search, RefreshCw, CheckCircle2,
  AlertTriangle, XCircle, MinusCircle, Radio, Zap, PauseCircle, ChevronDown, User, Users, X,
} from "lucide-react";
import { toast } from "sonner";

const REFRESH_MS = 10000;

// Pre-written SMS templates — save the partners a ton of typing when
// they're firing off quick updates from the console.
const SMS_TEMPLATES = [
  {
    label: "Build complete",
    body: "Hey {{name}} — your engine build is complete. Reply CALL and we'll walk it through together. — NEX",
  },
  {
    label: "Billing statement",
    body: "Hey {{name}} — your latest NEX billing statement is ready. Reply STATEMENT and we'll send it right over.",
  },
  {
    label: "Scheduling nudge",
    body: "Hey {{name}} — quick nudge to lock in a time. Grab a slot here: https://cal.com/nex/quick. — NEX",
  },
  {
    label: "Payment received",
    body: "Payment received, {{name}} — we're kicking off your build now. Expect the next update within 48h. — NEX",
  },
  {
    label: "Check-in",
    body: "Hey {{name}} — checking in from NEX. Anything blocking you this week? Reply here anytime.",
  },
];

const statusMeta = {
  sent:                { label: "Sent",             color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30", Icon: CheckCircle2 },
  gateway_error:       { label: "Gateway error",    color: "text-red-300",     bg: "bg-red-500/10 border-red-500/30",         Icon: XCircle },
  failure:             { label: "Failure",          color: "text-red-300",     bg: "bg-red-500/10 border-red-500/30",         Icon: XCircle },
  invalid_phone:       { label: "Invalid phone",    color: "text-amber-300",   bg: "bg-amber-500/10 border-amber-500/30",     Icon: AlertTriangle },
  no_gateway:          { label: "No gateway",       color: "text-amber-300",   bg: "bg-amber-500/10 border-amber-500/30",     Icon: AlertTriangle },
  skipped_disabled:    { label: "Skipped · disabled", color: "text-nex-muted", bg: "bg-white/5 border-white/10",              Icon: PauseCircle },
  skipped_optout:      { label: "Skipped · opt-out", color: "text-nex-muted",  bg: "bg-white/5 border-white/10",              Icon: MinusCircle },
  skipped_no_phone:    { label: "Skipped · no phone", color: "text-nex-muted", bg: "bg-white/5 border-white/10",              Icon: MinusCircle },
  skipped_category_optout: { label: "Skipped · cat opt-out", color: "text-nex-muted", bg: "bg-white/5 border-white/10",       Icon: MinusCircle },
  queued:              { label: "Queued",           color: "text-nex-chrome",  bg: "bg-white/5 border-white/10",              Icon: Loader2 },
};

const StatusBadge = ({ status }) => {
  const m = statusMeta[status] || { label: status, color: "text-nex-chrome", bg: "bg-white/5 border-white/10", Icon: MessageSquare };
  const { Icon } = m;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 border ${m.bg} ${m.color} mono-body text-[10px] tracking-[0.1em] uppercase`}
      data-testid={`status-badge-${status}`}
    >
      <Icon size={11} /> {m.label}
    </span>
  );
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso.slice(0, 16); }
};

const SmsConsolePage = () => {
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");   // "" = default (hide disabled)
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Direct Messaging widget
  const [testMsg, setTestMsg] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [lastTest, setLastTest] = useState(null);
  const [directory, setDirectory] = useState({ contacts: [], staff: [] });
  const [selectedRecipient, setSelectedRecipient] = useState(null); // { id, kind, name, phone, subtitle }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const pickerRef = useRef(null);
  const timerRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "200");
      if (statusFilter) {
        params.set("status", statusFilter);
      } else {
        params.set("hide_disabled", "true");
      }
      if (search.trim()) params.set("search", search.trim());
      const [cfg, sum, logs, dir] = await Promise.all([
        apiClient.get("/admin/sms/config"),
        apiClient.get("/admin/sms/summary?days=7"),
        apiClient.get(`/admin/sms/logs?${params.toString()}`),
        apiClient.get("/admin/sms/recipients"),
      ]);
      setConfig(cfg.data);
      setSummary(sum.data);
      setItems(logs.data.items || []);
      setDirectory({
        contacts: dir.data.contacts || [],
        staff: dir.data.staff || [],
      });
    } catch (err) {
      if (!silent) toast.error(formatApiError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial + on filter change
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(true), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    timerRef.current = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [autoRefresh, statusFilter, search]);

  // Close the recipient picker when the user clicks outside it.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [pickerOpen]);

  const applyTemplate = (tpl) => {
    // Mail-merge the recipient's first name so templates feel personal.
    const first = (selectedRecipient?.name || "").split(" ")[0] || "there";
    setTestMsg(tpl.body.replaceAll("{{name}}", first));
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!selectedRecipient) {
      return toast.error("Pick a recipient from the directory before sending.");
    }
    if (!testMsg.trim()) {
      return toast.error("Write a message (or pick a template) before sending.");
    }
    setTestBusy(true);
    setLastTest(null);
    try {
      const { data } = await apiClient.post("/admin/sms/test", {
        phone: selectedRecipient.phone,
        message: testMsg.trim(),
        recipient_name: selectedRecipient.name,
        recipient_kind: selectedRecipient.kind,
      });
      setLastTest(data);
      if (data.status === "sent") toast.success(`SMS sent to ${selectedRecipient.name}`);
      else toast.warning(`Status: ${data.status}${data.error ? ` — ${data.error}` : ""}`);
      await load(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTestBusy(false);
    }
  };

  const isDormant = config && !config.enabled;
  const isMisconfigured = config && config.enabled && !config.has_credentials;

  // Filter directory groups by the picker's search input.
  const filteredDirectory = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    const match = (p) => {
      if (!q) return true;
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.phone || "").toLowerCase().includes(q) ||
        (p.subtitle || "").toLowerCase().includes(q)
      );
    };
    return {
      contacts: directory.contacts.filter(match),
      staff: directory.staff.filter(match),
    };
  }, [directory, pickerQuery]);

  const directoryTotal = (directory.contacts?.length || 0) + (directory.staff?.length || 0);

  const statusOptions = useMemo(() => ([
    { value: "",                            label: "Default (hide disabled)" },
    { value: "sent",                        label: "Sent only" },
    { value: "gateway_error,failure",       label: "Errors only" },
    { value: "skipped_disabled",            label: "Skipped · disabled" },
    { value: "skipped_no_phone,invalid_phone,no_gateway", label: "Config issues" },
  ]), []);

  return (
    <div className="min-h-screen bg-[#0B0F19] p-6 md:p-10" data-testid="sms-console-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/admin" className="mono-body text-[11px] text-nex-chrome hover:text-nex-gold inline-flex items-center gap-2 mb-6">
          <ArrowLeft size={14} /> Back to Console
        </Link>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="mono-label mb-2">// SMS DISPATCH CONSOLE</div>
            <h1 className="serif-head text-3xl md:text-4xl">Live SMS Feed</h1>
            <p className="text-nex-chrome mt-2 text-sm">
              Direct messaging + broadcast log · dual-partner visibility · powered by SMSGate Cloud
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="mono-body text-[10px] uppercase tracking-[0.2em] text-nex-chrome inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="accent-nex-gold"
                data-testid="sms-auto-refresh-toggle"
              />
              Auto-refresh 10s
            </label>
            <button
              onClick={() => load()}
              disabled={refreshing}
              data-testid="sms-refresh-btn"
              className="tick-btn-secondary text-xs !py-2 !px-3"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
        ) : (
          <>
            {/* STATUS BANNER */}
            <div
              className={`card-industrial corner-tick p-5 mb-8 flex flex-col md:flex-row md:items-center gap-4 ${isDormant ? "border-amber-500/40" : isMisconfigured ? "border-red-500/40" : "border-emerald-500/30"}`}
              data-testid="sms-status-banner"
            >
              <div className="flex items-center gap-3">
                {isDormant ? (
                  <><PauseCircle className="text-amber-300" size={22} /><span className="mono-label !text-amber-200">// DORMANT</span></>
                ) : isMisconfigured ? (
                  <><AlertTriangle className="text-red-300" size={22} /><span className="mono-label !text-red-200">// MISCONFIGURED</span></>
                ) : (
                  <><Radio className="text-emerald-300 animate-pulse-gold" size={22} /><span className="mono-label !text-emerald-200">// LIVE</span></>
                )}
              </div>
              <div className="flex-1 text-sm text-nex-chrome">
                {isDormant ? (
                  <>SMS engine is <span className="text-amber-300">dormant</span> — set <code className="text-nex-gold">SMS_ENABLED=true</code> plus SMSGate creds in <code>backend/.env</code> to go live.</>
                ) : isMisconfigured ? (
                  <>SMS is enabled but no gateway credentials found. Set <code className="text-nex-gold">SMSGATE_USERNAME</code> and <code className="text-nex-gold">SMSGATE_PASSWORD</code>.</>
                ) : (
                  <>Provider <span className="text-emerald-300">{config?.provider}</span> · Sender label <span className="text-nex-gold">{config?.sender_label || "—"}</span> · Rate limit <span className="text-nex-silver">{config?.queue_delay_seconds}s</span> between sends</>
                )}
              </div>
              <div className="mono-body text-[11px] text-nex-chrome">
                Admin phones: <span className="text-nex-silver">{(config?.admin_phones?.length ?? 0)}</span>
              </div>
            </div>

            {/* SUMMARY METRICS (7-day) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="sms-summary">
              <SummaryCard label="Total 7d"   value={summary?.total ?? 0} accent="silver" testid="sms-total" />
              <SummaryCard label="Sent"       value={summary?.sent ?? 0}  accent="emerald" testid="sms-sent" />
              <SummaryCard label="Errors"     value={summary?.errors ?? 0} accent="red"    testid="sms-errors" />
              <SummaryCard label="Skipped"    value={summary?.skipped ?? 0} accent="muted" testid="sms-skipped" />
            </div>

            {/* DIRECT MESSAGING */}
            <form onSubmit={sendTest} className="card-industrial corner-tick p-5 md:p-6 mb-8" data-testid="sms-direct-form">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Zap className="text-nex-gold" size={16} />
                  <div className="mono-label">// DIRECT MESSAGING</div>
                </div>
                <div className="mono-body text-[10px] text-nex-muted">
                  {directoryTotal} recipient{directoryTotal === 1 ? "" : "s"} on file
                </div>
              </div>

              {/* RECIPIENT PICKER */}
              <div className="relative mb-3" ref={pickerRef}>
                <button
                  type="button"
                  onClick={() => setPickerOpen((o) => !o)}
                  data-testid="sms-direct-recipient-btn"
                  className="input-industrial w-full flex items-center justify-between !py-2.5 text-left"
                >
                  {selectedRecipient ? (
                    <div className="flex items-center gap-2 min-w-0">
                      {selectedRecipient.kind === "staff"
                        ? <Users size={14} className="text-nex-gold shrink-0" />
                        : <User size={14} className="text-nex-silver shrink-0" />}
                      <span className="text-nex-silver truncate">{selectedRecipient.name}</span>
                      <span className="mono-body text-[10px] text-nex-muted shrink-0">· {selectedRecipient.phone}</span>
                    </div>
                  ) : (
                    <span className="text-nex-muted text-sm">Pick a customer or staff member…</span>
                  )}
                  <div className="flex items-center gap-2 shrink-0">
                    {selectedRecipient && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setSelectedRecipient(null); }}
                        data-testid="sms-direct-clear-btn"
                        className="text-nex-muted hover:text-red-300 p-1 cursor-pointer"
                      >
                        <X size={13} />
                      </span>
                    )}
                    <ChevronDown size={14} className={`text-nex-chrome transition-transform ${pickerOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>

                {pickerOpen && (
                  <div
                    className="absolute z-30 mt-2 w-full card-industrial border border-nex-border/70 shadow-xl max-h-96 overflow-hidden flex flex-col"
                    data-testid="sms-direct-picker"
                  >
                    <div className="relative border-b border-nex-border/50 shrink-0">
                      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-nex-muted" />
                      <input
                        autoFocus
                        type="text"
                        value={pickerQuery}
                        onChange={(e) => setPickerQuery(e.target.value)}
                        placeholder="Search by name, phone, or company"
                        data-testid="sms-direct-picker-search"
                        className="w-full bg-transparent pl-9 pr-3 py-2.5 text-sm text-nex-silver placeholder:text-nex-muted focus:outline-none"
                      />
                    </div>
                    <div className="overflow-y-auto">
                      {filteredDirectory.contacts.length === 0 && filteredDirectory.staff.length === 0 ? (
                        <div className="text-center py-8 text-nex-muted text-sm">
                          No matches. Add phone numbers on Contacts or Staff to see them here.
                        </div>
                      ) : (
                        <>
                          {filteredDirectory.contacts.length > 0 && (
                            <div>
                              <div className="mono-label !text-[9px] px-3 py-2 bg-black/30 border-b border-nex-border/40 sticky top-0">
                                Customers · {filteredDirectory.contacts.length}
                              </div>
                              {filteredDirectory.contacts.map((p) => (
                                <RecipientRow
                                  key={`c-${p.id}`}
                                  person={p}
                                  onPick={() => { setSelectedRecipient(p); setPickerOpen(false); setPickerQuery(""); }}
                                />
                              ))}
                            </div>
                          )}
                          {filteredDirectory.staff.length > 0 && (
                            <div>
                              <div className="mono-label !text-[9px] px-3 py-2 bg-black/30 border-b border-nex-border/40 sticky top-0">
                                Staff · {filteredDirectory.staff.length}
                              </div>
                              {filteredDirectory.staff.map((p) => (
                                <RecipientRow
                                  key={`s-${p.id}`}
                                  person={p}
                                  onPick={() => { setSelectedRecipient(p); setPickerOpen(false); setPickerQuery(""); }}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* MESSAGE + TEMPLATES */}
              <div className="grid md:grid-cols-[1fr_auto] gap-3">
                <textarea
                  data-testid="sms-direct-message"
                  value={testMsg}
                  onChange={(e) => setTestMsg(e.target.value)}
                  placeholder="Optional custom message (defaults to system test payload if you leave it blank and no template is picked)"
                  rows={3}
                  className="input-industrial resize-none"
                />
                <button
                  type="submit"
                  disabled={testBusy || !selectedRecipient}
                  data-testid="sms-direct-submit"
                  className="tick-btn-primary text-xs !py-2 !px-4 justify-center self-start"
                >
                  {testBusy ? <><Loader2 size={13} className="animate-spin" /> Sending</> : <>Send <Send size={13} /></>}
                </button>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="sms-direct-templates">
                <span className="mono-body text-[10px] uppercase tracking-[0.15em] text-nex-muted mr-1">Templates:</span>
                {SMS_TEMPLATES.map((tpl) => (
                  <button
                    type="button"
                    key={tpl.label}
                    onClick={() => applyTemplate(tpl)}
                    data-testid={`sms-template-${tpl.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="mono-body text-[10px] uppercase tracking-[0.1em] px-2.5 py-1 border border-nex-border/60 text-nex-chrome hover:border-nex-gold hover:text-nex-gold transition-colors"
                  >
                    {tpl.label}
                  </button>
                ))}
              </div>

              {lastTest && (
                <div className="mt-4 mono-body text-[11px] text-nex-chrome flex items-center gap-3" data-testid="sms-direct-result">
                  <StatusBadge status={lastTest.status} />
                  {lastTest.provider_message_id && <span>id: <span className="text-nex-silver">{lastTest.provider_message_id}</span></span>}
                  {lastTest.error && <span className="text-red-300">{lastTest.error}</span>}
                </div>
              )}
            </form>

            {/* FILTERS */}
            <div className="flex flex-col md:flex-row gap-3 mb-4" data-testid="sms-filters">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nex-muted" />
                <input
                  type="text"
                  data-testid="sms-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search phone, preview, or recipient name"
                  className="input-industrial !pl-9"
                />
              </div>
              <select
                data-testid="sms-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input-industrial md:w-64"
              >
                {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {/* LOG FEED */}
            <div className="card-industrial corner-tick overflow-hidden" data-testid="sms-log-table">
              {items.length === 0 ? (
                <div className="text-center py-16">
                  <MessageSquare className="text-nex-muted mx-auto mb-3" size={26} />
                  <p className="text-nex-chrome text-sm">
                    {isDormant
                      ? "SMS is dormant. Once activated, every dispatch will stream here in real time."
                      : "No messages match your filters yet."}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-black/30 border-b border-nex-border">
                      <tr className="mono-body text-[10px] uppercase tracking-[0.15em] text-nex-chrome">
                        <th className="text-left px-4 py-3">When</th>
                        <th className="text-left px-4 py-3">Category</th>
                        <th className="text-left px-4 py-3">Recipient</th>
                        <th className="text-left px-4 py-3">Preview</th>
                        <th className="text-left px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row) => (
                        <tr key={row.id} className="border-b border-nex-border/50 hover:bg-white/[0.02]" data-testid={`sms-row-${row.id}`}>
                          <td className="px-4 py-3 mono-body text-[11px] text-nex-chrome whitespace-nowrap">{fmtTime(row.at)}</td>
                          <td className="px-4 py-3">
                            <span className="mono-body text-[10px] uppercase tracking-[0.1em] text-nex-gold">{row.category || "—"}</span>
                          </td>
                          <td className="px-4 py-3 mono-body text-[11px] text-nex-silver whitespace-nowrap">
                            {row.recipient_phone || "—"}
                            {row.recipient_name && <span className="text-nex-muted ml-2">· {row.recipient_name}</span>}
                          </td>
                          <td className="px-4 py-3 text-nex-chrome max-w-[420px] truncate" title={row.message_preview}>
                            {row.message_preview || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={row.status} />
                            {row.error && <div className="mono-body text-[10px] text-red-300 mt-1 truncate max-w-[260px]" title={row.error}>{row.error}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mono-body text-[10px] text-nex-muted text-center mt-4">
              Showing {items.length} of latest dispatches · every send is audited in <code>sms_message_logs</code>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const RecipientRow = ({ person, onPick }) => (
  <button
    type="button"
    onClick={onPick}
    data-testid={`sms-direct-picker-row-${person.kind}-${person.id}`}
    className="w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-nex-border/30 hover:bg-nex-gold/10 transition-colors"
  >
    <div className="w-8 h-8 border border-nex-border/50 flex items-center justify-center shrink-0">
      {person.kind === "staff"
        ? <Users size={13} className="text-nex-gold" />
        : <User size={13} className="text-nex-silver" />}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm text-nex-silver truncate">{person.name}</div>
      {person.subtitle && (
        <div className="mono-body text-[10px] text-nex-muted truncate">{person.subtitle}</div>
      )}
    </div>
    <div className="mono-body text-[10px] text-nex-chrome shrink-0">{person.phone}</div>
  </button>
);

const SummaryCard = ({ label, value, accent = "silver", testid }) => {
  const accentClass = {
    silver:  "text-nex-silver",
    emerald: "text-emerald-300",
    red:     "text-red-300",
    muted:   "text-nex-muted",
    gold:    "text-nex-gold",
  }[accent] || "text-nex-silver";
  return (
    <div className="card-industrial p-4" data-testid={testid}>
      <div className="mono-label mb-2 !text-[9px]">{label}</div>
      <div className={`serif-head text-3xl ${accentClass}`}>{value}</div>
    </div>
  );
};

export default SmsConsolePage;
